import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { requestBuilder } from '../../http/RequestBuilder';
import { FreeToSpendDataAtom } from '../state/FreeToSpendAtoms';
import { type FreeToSpendData } from '../types/FreeToSpendTypes';

const getFreeToSpendData = requestBuilder.query<
    FreeToSpendData,
    { minutesOffset: number }
>({
    endpoint: ({ minutesOffset }) =>
        `/api/holistic/free-to-spend?minutesOffset=${minutesOffset}`,
    queryKey: () => 'getFreeToSpendData',
    assertResponse: (data) => {
        // fetcher resolves a blank body as ''. Throw so this query retries.
        if (data === '') {
            throw new Error('Received an empty free-to-spend response');
        }
    },
});

const isFreeToSpendData = (value: unknown): value is FreeToSpendData =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

export const useGetFreeToSpendData = (enabled = false) => {
    const [freeToSpendData, setFreeToSpendData] = useAtom(FreeToSpendDataAtom);
    const minutesOffset = new Date().getTimezoneOffset();

    const { data, isLoading, isError, error, refetch } = getFreeToSpendData(
        {
            minutesOffset,
        },
        { enabled },
    );

    useEffect(() => {
        if (!isLoading && isFreeToSpendData(data)) {
            setFreeToSpendData(data);
        }
    }, [data, isLoading]);

    // A blank body throws above and retries. Anything else that is not spend
    // data still fails this card without being stored.
    const receivedEmptyBody =
        !isLoading && data != null && !isFreeToSpendData(data);

    return {
        data: freeToSpendData,
        isLoading,
        isError: isError || receivedEmptyBody,
        error,
        refetch,
    };
};

export default useGetFreeToSpendData;
